import React from 'react';

import CheckIcon from '@material-ui/icons/Check';
import {
  DiscriminatorType,
  MobxZodDiscriminatedUnion,
  MobxZodField,
  decodeResultEqual,
} from '@monoid-dev/mobx-zod-form';
import { makeMuiStyles, useCommonStyles } from '@styles';
import { createStrings } from '@utils/local';
import clsx from 'clsx';
import Color from 'color';
import { observer } from 'mobx-react';
import { ZodBoolean, ZodString, z } from 'zod';

const strings = createStrings({
  ja: {
    choose: '選択する',
    chosen: '選択済み',
  },
});

const useStyles = makeMuiStyles((theme, common) => ({
  cardCheckboxContainer: {
    ...common.flexInline,
    ...common.baseButton,
    border: `1px solid ${theme.palette.grey[300]}`,
    borderRadius: 4,
    '&$disabled': {
      cursor: 'not-allowed',
    },
    '&$checked': {
      borderColor: theme.palette.primary.main,
      backgroundColor: Color(theme.palette.primary.main).alpha(0.08).toString(),
      '&$disabled': {
        borderColor: theme.palette.grey[500],
        backgroundColor: Color(theme.palette.grey[500]).alpha(0.03).toString(),
      },
    },
  },
  cardCheckboxLabelContainer: {
    flex: 1,
  },
  cardCheckboxCheckerContainer: {
    ...common.flexColumnCenter,
    padding: 20,
    paddingLeft: 10,
  },
  cardCheckboxChecker: {
    ...common.flexCenter,
    ...common.textPrimary,
    ...common.smallerText,
    width: 84,
    height: 28,
    borderRadius: 2,
    border: `1px solid ${theme.palette.primary.main}`,
    backgroundColor: theme.palette.background.paper,
    '$checked &': {
      border: `1px solid ${theme.palette.common.white}`,
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.common.white,
    },
    '$disabled$checked &': {
      backgroundColor: theme.palette.grey[500],
      color: theme.palette.common.white,
    },
    '$disabled &': {
      border: `1px solid ${theme.palette.grey[500]}`,
      color: theme.palette.grey[500],
    },
  },
  checked: {},
  small: {},
  big: {},
  disabled: {},
}));

export interface CardCheckboxProps<
  T extends ZodString | ZodBoolean | DiscriminatorType<MobxZodDiscriminatedUnion>,
> {
  field: MobxZodField<T>;
  value: z.infer<T>;
  label: React.ReactNode;
  size?: 'small' | 'big';
  disabled?: boolean;
  onSelected?: () => void;
}

export const CardCheckbox = observer(
  <T extends ZodString | ZodBoolean | DiscriminatorType<MobxZodDiscriminatedUnion>>(
    props: CardCheckboxProps<T>,
  ) => {
    const { field, value, label, size, disabled, onSelected } = props;

    const classes = {
      ...useCommonStyles(),
      ...useStyles(),
    };

    const checked = decodeResultEqual(field.decodeResult, value);

    return (
      <div
        className={clsx(
          classes.cardCheckboxContainer,
          checked && classes.checked,
          disabled && classes.disabled,
          classes[size ?? 'big'],
        )}
        onClick={
          disabled
            ? undefined
            : () => {
                field.setOutput(value);
                field.setTouched(true);

                onSelected?.();
              }
        }
      >
        <div className={classes.cardCheckboxLabelContainer}>{label}</div>
        <div className={classes.cardCheckboxCheckerContainer}>
          <div data-testid="CardCheckboxChecker" className={classes.cardCheckboxChecker}>
            {!checked && <span>{strings.choose}</span>}
            {checked && (
              <>
                <CheckIcon fontSize="small" /> <span>{strings.chosen}</span>
              </>
            )}
          </div>
        </div>
      </div>
    );
  },
);
